import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import { monday } from './services/monday';

// monday passes the session token (a credential) in the iframe URL, and Sentry copies the page URL into events,
// breadcrumbs and replays. dataCollection.urlQueryParams doesn't cover those URLs, so the value is replaced here.
const SESSION_TOKEN_PATTERN = /(sessionToken=)[^&#"\s]*/g;

// only walks arrays and plain objects (breadcrumb data can hold DOM nodes, class instances or cycles)
function scrubSessionToken<T>(value: T, depth = 0): T {
  if (typeof value === 'string') {
    return (value.includes('sessionToken=') ? value.replace(SESSION_TOKEN_PATTERN, '$1[Filtered]') : value) as T;
  }
  if (depth >= 10) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubSessionToken(item, depth + 1)) as T;
  }
  if (_.isPlainObject(value)) {
    return _.mapValues(value as object, (item) => scrubSessionToken(item, depth + 1)) as T;
  }
  return value;
}

if (import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: 'https://bdaa97ac37a3400e9473076f39e304d9@o149455.ingest.sentry.io/4505605572591616',
    // lifecycle 'route' keeps the v10 behaviour (a new session on every navigation); v11 defaults to 'page'
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // only sees the recording's breadcrumb/span events; its meta event (href) still carries the token
        beforeAddRecordingEvent: (event) => scrubSessionToken(event),
      }),
      Sentry.browserSessionIntegration({ lifecycle: 'route' }),
    ],
    // v11 collects user info, cookies, headers and bodies when this is unset; this is the v10 default from the
    // migration guide (https://github.com/getsentry/sentry-javascript/blob/develop/MIGRATION.md#sendDefaultPii-is-replaced-by-dataCollection)
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
      frameContextLines: 7,
    },
    tracePropagationTargets: ['localhost'],
    tracesSampleRate: 0, // Capture 100% of the transactions, reduce in production!
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // monday-sdk-js >= 0.5.9 warns on every monday.api() call; keep those out of the breadcrumb buffer
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.message?.startsWith('[DEPRECATION WARNING]')) {
        return null;
      }
      return scrubSessionToken(breadcrumb);
    },
  });

  // runs for error and replay events alike (beforeSend only sees errors)
  Sentry.addEventProcessor((event) => scrubSessionToken(event));

  monday.get('context').then(({ data }) => {
    const id = `${_.get(data, 'account.id')}_${_.get(data, 'user.id')}`;
    Sentry.setUser({ id: id, user_id: _.get(data, 'user.id'), account_id: _.get(data, 'account.id') });
  });
}
